/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { Player } from '@msdyn365-commerce/components';
import { IVideoPlayerAdditionalContentViewProps } from './components/additional-content';
import { IVideoPlayerViewProps } from './mod-video-player';

/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */
const renderAdditionalContent = (additionalContent: IVideoPlayerAdditionalContentViewProps) => {
    if (additionalContent.additionalContentItems) {
        return (
            <Node {...additionalContent.additionalContentNode}>
                <>
                    {additionalContent.additionalContentItems.heading}
                    <Node {...additionalContent.additionalContentItems.additionalContentItemContainer}>
                        {additionalContent.additionalContentItems.text}
                        <Node {...additionalContent.additionalContentItems.additionalContentItemLinks}>
                            {additionalContent.additionalContentItems.links}
                        </Node>
                    </Node>
                </>
            </Node>
        );
    }

    return null;
};

const Icon = ({ type }: { type: 'paused' | 'play' }) => {
    if (type !== 'paused') {
        return (
            <svg fill='currentColor' width={15} height={15} xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'>
                <path d='M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm0 448c-110.5 0-200-89.5-200-200S145.5 56 256 56s200 89.5 200 200-89.5 200-200 200zm96-280v160c0 8.8-7.2 16-16 16h-48c-8.8 0-16-7.2-16-16V176c0-8.8 7.2-16 16-16h48c8.8 0 16 7.2 16 16zm-112 0v160c0 8.8-7.2 16-16 16h-48c-8.8 0-16-7.2-16-16V176c0-8.8 7.2-16 16-16h48c8.8 0 16 7.2 16 16z' />
            </svg>
        );
    }
    return (
        <svg fill='currentColor' width={15} height={15} xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'>
            <path d='M371.7 238l-176-107c-15.8-8.8-35.7 2.5-35.7 21v208c0 18.4 19.8 29.8 35.7 21l176-101c16.4-9.1 16.4-32.8 0-42zM504 256C504 119 393 8 256 8S8 119 8 256s111 248 248 248 248-111 248-248zm-448 0c0-110.5 89.5-200 200-200s200 89.5 200 200-89.5 200-200 200S56 366.5 56 256z' />
        </svg>
    );
};

const VideoPlayerView: React.FC<IVideoPlayerViewProps & { playerRef: React.LegacyRef<Player> }> = props => {
    const { config, posterUrl } = props;
    const { secondVideo, video } = config;
    const { _links } = secondVideo ?? {};
    const [secondVideoLink] = _links?.binaryReferences ?? [];
    const videoSrcs = [video, secondVideo ?? {}].map(({ _links }) => _links?.binaryReferences?.[0].clientHref ?? '');
    const [playerState, setPlayerState] = React.useState<'play' | 'paused'>('play');
    const [currentPlayer, setCurrentPlayer] = React.useState(0);
    const [isInit, setIsInit] = React.useState(true);
    const videoElem = React.useRef<HTMLVideoElement>(null);
    const secondVideoElem = React.useRef<HTMLVideoElement>(null);

    React.useEffect(() => {
        if (!secondVideoLink) {
            setPlayerState((props.playerRef as { current: any }).current.playerState);
        }
        videoElem.current?.pause();
        videoElem.current?.play();
        setIsInit(false);
    }, []);

    return (
        <div id={props.id} className='ms-video-player' {...props.renderModuleAttributes(props)}>
            <button
                onClick={() => {
                    if (!secondVideo) {
                        const { current } = props.playerRef as { current: any };
                        if (current.playerState === 'paused') {
                            setPlayerState('play');
                            current.play();
                        }
                        if (current.playerState !== 'paused') {
                            setPlayerState('paused');
                            current.pause();
                        }
                        return;
                    }
                    const elem = currentPlayer === 0 ? videoElem.current : secondVideoElem.current;
                    void elem?.[elem.paused ? 'play' : 'pause']();
                    setPlayerState(elem?.paused ? 'paused' : 'play');
                }}
            >
                <span>
                    <Icon type={playerState} />
                </span>
                <span>Reduce motion</span>
            </button>
            {!secondVideoLink ? props.player : null}
            {secondVideoLink ? (
                <div className='cps-hero-video__two-videos-container'>
                    {videoSrcs.map((url, i) => (
                        <video
                            ref={i === 0 ? videoElem : secondVideoElem}
                            className={`msc-videoplayer__video${isInit ? '' : ' no-blur'}${currentPlayer === i ? ' show' : ''}`}
                            key={url}
                            src={url}
                            muted
                            preload='auto'
                            autoPlay={i === 0}
                            poster={`${posterUrl}&q=15`}
                            onPlaying={() => void videoElem.current?.classList.add('no-blur')}
                            onEnded={() => {
                                setCurrentPlayer(state => (state === 0 ? 1 : 0));
                                void (currentPlayer === 0 ? secondVideoElem : videoElem).current?.play();
                            }}
                            aria-hidden='true'
                        />
                    ))}
                </div>
            ) : null}
            <Node {...props.detailsContainer} className={props.detailsContainer ? props.detailsContainer.className : ''}>
                {props.title}
                {props.text}
                {props.additionalContent && renderAdditionalContent(props.additionalContent)}
                <Node {...props.detailsContainerCta} className={props.detailsContainerCta ? props.detailsContainerCta.className : ''}>
                    {props.links}
                </Node>
            </Node>
        </div>
    );
};

export default VideoPlayerView;
